import posthog from 'posthog-js'
import { ERROR_NOISE_PATTERNS } from '@mondaygpt/constants'
import type { EventProperties, UserProperties } from '../mixpanel/types'
import { PosthogLogShipper, type PosthogLogLevel } from './posthog-logs'

export interface PostHogClientConfig {
  apiKey: string
  apiHost?: string
  debug?: boolean
}

const DEFAULT_HOST = 'https://us.i.posthog.com'

// Mixpanel-reserved person keys → PostHog's unprefixed equivalents, so PostHog
// persons stay searchable by name/email
const PERSON_KEY_MAP: Record<string, string> = {
  $name: 'name',
  $email: 'email',
}

/**
 * PostHog client service, dual-write twin of the Mixpanel wrapper during the
 * PostHog consolidation parallel-run. Every method no-ops until init().
 */
// A dead Logs lane must be visible somewhere: one console.error per interval
// (an Error, so the console bridge routes it to the ISSUE lane via posthog-js,
// never back into the shipper). Adblocked tabs fail every flush; keep it rare.
const LOGS_FAILURE_REPORT_INTERVAL_MS = 10 * 60 * 1000

class PostHogClientService {
  private initialized = false
  private logs: PosthogLogShipper | null = null
  private lastLogsFailureReportAt = 0

  init(config: PostHogClientConfig): void {
    if (this.initialized) {
      return
    }

    try {
      // Warning-class signal (console.warn bridge, explicit warning captures)
      // lives in PostHog Logs, never the error inbox — see posthog-logs.ts.
      this.logs = new PosthogLogShipper({
        token: config.apiKey,
        host: config.apiHost || DEFAULT_HOST,
        resource: {
          'service.name': 'client',
          ...(import.meta.env.VITE_APP_BUILD_NUMBER
            ? { build: String(import.meta.env.VITE_APP_BUILD_NUMBER) }
            : {}),
        },
        onFailure: reason => this.reportLogsFailure(reason),
      })
      if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', () => this.logs?.flush())
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') this.logs?.flush()
        })
      }

      posthog.init(config.apiKey, {
        api_host: config.apiHost || DEFAULT_HOST,
        defaults: '2025-05-24',
        // Anonymous events are ~4x cheaper; profiles only exist after identify()
        person_profiles: 'identified_only',
        // We track 150+ explicit typed events; autocapture would only add noise + cost
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        // Session replay: PostHog is the app's only recorder (LogRocket retired
        // in the consolidation's P7 sunset, 2026-09)
        disable_session_recording: false,
        session_recording: {
          // Typed input is the product signal (composer prompts, onboarding URLs).
          // Passwords stay masked; card fields live in Stripe/RC iframes rrweb
          // can't see. Scalpel for anything sensitive a spot-check surfaces:
          // ph-no-capture on the element.
          maskAllInputs: false,
          // Sanitized network capture: PostHog auto-redacts Authorization/Cookie
          // and scrubs known secret patterns, 1MB body cap
          recordHeaders: true,
          recordBody: true,
          // Canvas recording stays off (the default; composer previews are DOM/img)
        },
        enable_recording_console_log: true,
        // Native capture for globally-unhandled errors. console.error stays
        // off here — it routes through the error-reporting console bridge
        // (noise filters + rate limit + dedupe) instead.
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        // Feature flags load only for the logged-in identity (FeatureFlagProvider
        // reloads on identify); no anonymous /flags request on page load.
        advanced_disable_feature_flags_on_first_load: true,
        // Mixpanel runs with ignore_dnt: false; keep the tools in DNT parity
        respect_dnt: true,
        cross_subdomain_cookie: true,
        // Suppression parity with the error-reporting facade: natively-captured
        // exceptions bypass its filter, so the shared noise list must also
        // apply here (project-level ignore field caps at 300 chars)
        before_send: event => {
          if (event?.event === '$exception') {
            const exceptions = (event.properties?.$exception_list ?? []) as Array<{
              value?: string
            }>
            const isNoise = exceptions.some(
              e =>
                typeof e?.value === 'string' &&
                ERROR_NOISE_PATTERNS.some(pattern => pattern.test(e.value as string))
            )
            if (isNoise) return null
          }
          return event
        },
        debug: config.debug || false,
      })

      this.initialized = true
    } catch (error) {
      // Analytics must never take down the app boot path
      console.error('Failed to initialize PostHog:', error)
    }
  }

  isInitialized(): boolean {
    return this.initialized
  }

  /**
   * Current flag value. `fallback` is returned until flags have loaded and
   * whenever the flag is unknown to PostHog; a resolved value always wins.
   * posthog-js reports one `$feature_flag_called` per flag+value (deduped),
   * which feeds the flag's usage view in PostHog.
   */
  isFeatureEnabled(key: string, fallback: boolean): boolean {
    if (!this.initialized) {
      return fallback
    }

    try {
      return posthog.isFeatureEnabled(key, { defaultValue: fallback })
    } catch {
      return fallback
    }
  }

  /**
   * Subscribe to flag loads. Fires after every `/flags` response (success or
   * failure) and immediately if flags are already loaded. Returns unsubscribe.
   */
  onFeatureFlags(callback: () => void): () => void {
    if (!this.initialized) {
      return () => {}
    }

    try {
      return posthog.onFeatureFlags(() => callback())
    } catch {
      return () => {}
    }
  }

  reloadFeatureFlags(): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.reloadFeatureFlags()
    } catch (error) {
      console.error('Failed to reload PostHog feature flags:', error)
    }
  }

  getDistinctId(): string | undefined {
    if (!this.initialized) {
      return undefined
    }

    try {
      return posthog.get_distinct_id() || undefined
    } catch {
      return undefined
    }
  }

  capture(eventName: string, properties?: EventProperties): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.capture(eventName, properties)
    } catch (error) {
      console.error('Failed to capture PostHog event:', error)
    }
  }

  captureException(error: unknown, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.captureException(error, properties)
    } catch {
      // console.error here would loop back through the console bridge
      // into this very method; a failed telemetry send is not worth that risk
    }
  }

  /**
   * The posthog-js session id, sent to the API as `X-PostHog-Session-Id` so
   * server-side issues and logs link back to this browser's recording.
   */
  getSessionId(): string | undefined {
    if (!this.initialized) {
      return undefined
    }

    try {
      return posthog.get_session_id() || undefined
    } catch {
      return undefined
    }
  }

  /**
   * Ship one record to PostHog Logs (service `client`). Attaches the page and
   * the PostHog distinct id so a warning burst can be traced to sessions.
   */
  shipLog(level: PosthogLogLevel, message: string, attributes: Record<string, unknown> = {}): void {
    if (!this.initialized || !this.logs) {
      return
    }

    try {
      // The raw shipper has no consent gate of its own: honour the SDK's
      // (respect_dnt / opt-out) so Logs never sees a user the events don't.
      if (posthog.has_opted_out_capturing()) return
      // Session keys first: the record keeps its first 30 attributes, and
      // these two are the ones that make a warning burst traceable.
      this.logs.ship(level, message, {
        $current_url: typeof window !== 'undefined' ? window.location.href : undefined,
        distinct_id: posthog.get_distinct_id(),
        ...attributes,
      })
    } catch {
      // Same loop hazard as captureException: never console.* from here
    }
  }

  private reportLogsFailure(reason: string): void {
    const now = Date.now()
    if (now - this.lastLogsFailureReportAt < LOGS_FAILURE_REPORT_INTERVAL_MS) return
    this.lastLogsFailureReportAt = now
    console.error(new Error(`[posthog-logs] delivery to PostHog Logs failed: ${reason}`))
  }

  identify(userId: string): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.identify(userId)
    } catch (error) {
      console.error('Failed to identify PostHog user:', error)
    }
  }

  setPersonProperties(properties: UserProperties): void {
    if (!this.initialized) {
      return
    }

    try {
      const mapped = Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [PERSON_KEY_MAP[key] ?? key, value])
      )
      posthog.setPersonProperties(mapped)
    } catch (error) {
      console.error('Failed to set PostHog person properties:', error)
    }
  }

  /** Associate this device's subsequent events with a group (e.g. the business) */
  group(groupType: string, groupKey: string): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.group(groupType, groupKey)
    } catch (error) {
      console.error('Failed to set PostHog group:', error)
    }
  }

  register(properties: Record<string, string | number | boolean | null | undefined>): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.register(properties)
    } catch (error) {
      console.error('Failed to register PostHog super properties:', error)
    }
  }

  registerOnce(properties: Record<string, string | number | boolean | null | undefined>): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.register_once(properties)
    } catch (error) {
      console.error('Failed to register PostHog super properties once:', error)
    }
  }

  reset(): void {
    if (!this.initialized) {
      return
    }

    try {
      posthog.reset()
    } catch (error) {
      console.error('Failed to reset PostHog session:', error)
    }
  }
}

export const posthogClient = new PostHogClientService()
